import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import { supabase } from '../config/supabase';

// Session timeout based on user role
const getSessionTimeout = (userRole) => {
  if (userRole === 'paramedic') {
    return 24 * 60 * 60 * 1000; // 24 hours for paramedics
  }
  return 2 * 60 * 60 * 1000; // 2 hours for other users
};

// Global timer reference to manage session timeout
let sessionTimeoutTimer = null;

const useAuthStore = create(
    persist(
        (set, get) => ({
            // State
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            // Actions
            setLoading: (loading) => set({ isLoading: loading }),

            setError: (error) => set({ error }),

            clearError: () => set({ error: null }),

            // Start session timeout timer (role-based)
            startSessionTimeout: () => {
                // Clear any existing timer
                if (sessionTimeoutTimer) {
                    clearTimeout(sessionTimeoutTimer);
                    sessionTimeoutTimer = null;
                }

                const { user } = get();
                if (!user) return;

                // Get session timeout based on user role
                const SESSION_TIMEOUT = getSessionTimeout(user.role);

                // Get login time from localStorage or use current time
                const loginTimeKey = 'session_login_time';
                const storedLoginTime = localStorage.getItem(loginTimeKey);
                const loginTime = storedLoginTime ? parseInt(storedLoginTime, 10) : Date.now();
                
                // If no stored time, save current time
                if (!storedLoginTime) {
                    localStorage.setItem(loginTimeKey, loginTime.toString());
                }

                // Calculate elapsed time
                const elapsedTime = Date.now() - loginTime;
                const remainingTime = SESSION_TIMEOUT - elapsedTime;

                // If timeout has already passed, logout immediately
                if (remainingTime <= 0) {
                    const timeoutHours = user.role === 'paramedic' ? 24 : 2;
                    console.log(`⏰ Session timeout: ${timeoutHours} hours already elapsed, logging out immediately...`);
                    localStorage.removeItem(loginTimeKey);
                    // Set flag to show session timeout message
                    localStorage.setItem('session_timeout', 'true');
                    const { logout } = get();
                    logout().then(() => {
                        // Redirect to login page
                        window.location.href = '/login';
                    });
                    return;
                }

                // Set timer for remaining time
                sessionTimeoutTimer = setTimeout(() => {
                    const timeoutHours = user.role === 'paramedic' ? 24 : 2;
                    console.log(`⏰ Session timeout: ${timeoutHours} hours elapsed, logging out...`);
                    localStorage.removeItem(loginTimeKey);
                    // Set flag to show session timeout message
                    localStorage.setItem('session_timeout', 'true');
                    const { logout } = get();
                    logout().then(() => {
                        // Redirect to login page
                        window.location.href = '/login';
                    });
                }, remainingTime);

                const remainingMinutes = Math.floor(remainingTime / 1000 / 60);
                const timeoutHours = user.role === 'paramedic' ? 24 : 2;
                console.log(`⏰ Session timeout timer started: will expire in ${remainingMinutes} minutes (${Math.floor(remainingMinutes / 60)}h ${remainingMinutes % 60}m) for ${user.role} role`);
            },

            // Clear session timeout timer
            clearSessionTimeout: () => {
                if (sessionTimeoutTimer) {
                    clearTimeout(sessionTimeoutTimer);
                    sessionTimeoutTimer = null;
                    console.log('⏰ Session timeout timer cleared');
                }
                // Clear login time from localStorage
                localStorage.removeItem('session_login_time');
            },

            // Initialize auth state (check Supabase session)
            initialize: async () => {
                // Set loading immediately
                set({ isLoading: true });
                console.log('🔄 Initializing auth...');

                // CRITICAL: Guaranteed timeout wrapper - ensures isLoading is ALWAYS set to false
                // This prevents the app from being stuck on loading screen forever
                const timeoutId = setTimeout(() => {
                    console.warn('⚠️ Initialization timeout - forcing loading to false');
                    set({
                        isLoading: false,
                        user: null,
                        session: null,
                        isAuthenticated: false,
                        error: null
                    });
                }, 3000); // Maximum 3 seconds before forcing completion

                try {
                    // Check if Supabase is configured
                    console.log('🔍 Checking Supabase configuration...');
                    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
                    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

                    if (!supabaseUrl || !supabaseKey) {
                        console.error('❌ Supabase environment variables missing!');
                        clearTimeout(timeoutId);
                        set({
                            user: null,
                            session: null,
                            isAuthenticated: false,
                            isLoading: false,
                            error: 'Supabase configuration missing'
                        });
                        return;
                    }

                    console.log('✅ Supabase configured, checking session...');

                    // Check localStorage directly first to avoid hanging on getSession()
                    // This prevents blocking if there's any localStorage corruption or conflict
                    let sessionResult = { data: { session: null }, error: null };

                    try {
                        // Check if Supabase has stored session data in localStorage
                        const supabaseStorageKey = 'supabase-auth';
                        const storedData = localStorage.getItem(supabaseStorageKey);

                        if (!storedData) {
                            // No stored data - skip getSession() call entirely
                            console.log('✅ No stored session data - proceeding to login page');
                            sessionResult = { data: { session: null }, error: null };
                        } else {
                            // Data exists - try to get session with timeout protection
                            console.log('🔍 Stored session data found, fetching session...');

                            // Create a promise that always resolves within timeout
                            const sessionCheckPromise = supabase.auth.getSession().catch((err) => {
                                console.warn('⚠️ getSession() error:', err.message);
                                return { data: { session: null }, error: err };
                            });

                            // Fallback promise that resolves after timeout
                            const fallbackPromise = new Promise((resolve) => {
                                setTimeout(() => {
                                    console.warn('⚠️ Session check timeout - proceeding without session');
                                    resolve({ data: { session: null }, error: null });
                                }, 2000); // 2 second timeout
                            });

                            // Race between session check and timeout
                            sessionResult = await Promise.race([sessionCheckPromise, fallbackPromise]);

                            if (sessionResult?.data?.session) {
                                console.log('✅ Session retrieved successfully');
                            } else {
                                console.log('✅ No valid session - proceeding to login page');
                            }
                        }
                    } catch (error) {
                        console.warn('⚠️ Session check failed, proceeding to login:', error.message);
                        sessionResult = { data: { session: null }, error: null };
                    }

                    const { data: { session }, error: sessionError } = sessionResult || { data: { session: null }, error: null };

                    if (sessionError) {
                        console.error('❌ Session error:', sessionError);
                        // Don't block on error - just show login page
                        clearTimeout(timeoutId);
                        set({
                            user: null,
                            session: null,
                            isAuthenticated: false,
                            isLoading: false,
                            error: null
                        });
                        return;
                    }

                    if (!session) {
                        // No session - user needs to log in
                        console.log('✅ No session found - showing login page');
                        clearTimeout(timeoutId);
                        set({
                            user: null,
                            session: null,
                            isAuthenticated: false,
                            isLoading: false,
                            error: null
                        });
                        return;
                    }

                    // Fetch user details from backend (if session exists)
                    console.log('🔍 Session found, fetching user from backend...');
                    try {
                        api.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;

                        const response = await api.get('/auth/me');

                        console.log('✅ User data fetched successfully');
                        clearTimeout(timeoutId);
                        set({
                            user: response.data.data,
                            session,
                            isAuthenticated: true,
                            isLoading: false,
                            error: null
                        });

                        // Start session timeout timer (2 hours)
                        get().startSessionTimeout();
                    } catch (backendError) {
                        // Backend call failed - but we still have a Supabase session
                        console.warn('⚠️ Backend /auth/me failed:', backendError.message);
                        // Still set loading to false so user can see login page
                        clearTimeout(timeoutId);
                        set({
                            user: null,
                            session: null,
                            isAuthenticated: false,
                            isLoading: false,
                            error: null // Don't show error - just let them log in
                        });
                    }
                } catch (error) {
                    console.error('❌ Initialize error:', error);
                    clearTimeout(timeoutId);
                    // Always set loading to false on error so user can see login page
                    set({
                        user: null,
                        session: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: null // Don't show error - just let them log in
                    });
                }
            },

            // Set up auth state change listener
            setupAuthListener: () => {
                const { data: { subscription } } = supabase.auth.onAuthStateChange(
                    async (event, session) => {
                        // Only log important auth events (not initial SIGNED_OUT)
                        if (event === 'SIGNED_IN') {
                            console.log('✅ User signed in successfully');
                        } else if (event === 'SIGNED_OUT' && session) {
                            // Only log SIGNED_OUT if there was a previous session (user logged out)
                            console.log('👋 User signed out');
                        }
                        // Silent on initial SIGNED_OUT (no user logged in)

                        if (event === 'SIGNED_IN' && session) {
                            // Update session and fetch user data
                            try {
                                api.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
                                const response = await api.get('/auth/me');

                                set({
                                    user: response.data.data,
                                    session,
                                    isAuthenticated: true,
                                    error: null
                                });

                                // Start session timeout timer (2 hours)
                                get().startSessionTimeout();
                            } catch (error) {
                                console.error('Failed to fetch user data:', error);
                            }
                        } else if (event === 'SIGNED_OUT') {
                            // Clear session timeout timer on sign out
                            get().clearSessionTimeout();
                            set({
                                user: null,
                                session: null,
                                isAuthenticated: false,
                                error: null
                            });
                            delete api.defaults.headers.common['Authorization'];
                        } else if (event === 'TOKEN_REFRESHED' && session) {
                            // Update session with refreshed token
                            set({ session });
                            api.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
                        } else if (event === 'USER_UPDATED' && session) {
                            // Refetch user data when user is updated
                            try {
                                const response = await api.get('/auth/me');
                                set({ user: response.data.data });
                            } catch (error) {
                                console.error('Failed to fetch updated user data:', error);
                            }
                        }
                    }
                );

                // Return unsubscribe function
                return () => {
                    subscription.unsubscribe();
                };
            },

            // Login
            login: async (email, password) => {
                try {
                    set({ isLoading: true, error: null });

                    // Sign in with Supabase Auth
                    const { data, error: signInError } = await supabase.auth.signInWithPassword({
                        email,
                        password
                    });

                    if (signInError) {
                        throw signInError;
                    }

                    // Set authorization header
                    api.defaults.headers.common['Authorization'] = `Bearer ${data.session.access_token}`;

                    // Fetch user details from backend
                    const response = await api.get('/auth/me');
                    const user = response.data.data;

                    set({
                        user,
                        session: data.session,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null
                    });

                    // Start session timeout timer (2 hours)
                    get().startSessionTimeout();

                    return { success: true, data: { user, session: data.session } };
                } catch (error) {
                    console.error('Login error:', error);
                    // Backend'ten gelen mesajı (örn. nöbet planı yok) öne çıkar
                    const errorMessage =
                        error.response?.data?.error ||
                        error.response?.data?.message ||
                        error.message || 'Giriş başarısız';

                    // Erişim reddedilen kullanıcının Supabase oturumunu da kapat
                    try { await supabase.auth.signOut(); } catch (e) { /* yut */ }

                    set({
                        error: errorMessage,
                        isLoading: false,
                        isAuthenticated: false,
                        user: null,
                        session: null
                    });

                    delete api.defaults.headers.common['Authorization'];
                    throw error;
                }
            },

            // Logout
            logout: async () => {
                try {
                    // Clear session timeout timer
                    get().clearSessionTimeout();

                    // Sign out from Supabase Auth
                    await supabase.auth.signOut();
                } catch (error) {
                    console.warn('Logout error:', error);
                } finally {
                    // Clear state regardless of API call result
                    set({
                        user: null,
                        session: null,
                        isAuthenticated: false,
                        error: null,
                        isLoading: false
                    });

                    // Clear authorization header
                    delete api.defaults.headers.common['Authorization'];
                }
            },

            // Register (Admin only)
            register: async (userData) => {
                try {
                    set({ isLoading: true, error: null });

                    const response = await api.post('/auth/register', userData);

                    set({ isLoading: false });
                    return { success: true, data: response.data.data };
                } catch (error) {
                    const errorMessage = error.response?.data?.message || 'Kayıt başarısız';
                    set({ error: errorMessage, isLoading: false });
                    throw error;
                }
            },

            // Public signup (creates user in Supabase Auth, user record will be auto-created by middleware)
            signup: async (userData) => {
                try {
                    set({ isLoading: true, error: null });

                    // Sign up with Supabase Auth
                    const { data, error: signUpError } = await supabase.auth.signUp({
                        email: userData.email,
                        password: userData.password,
                        options: {
                            data: {
                                first_name: userData.first_name,
                                last_name: userData.last_name,
                                role: 'paramedic' // Default role for new signups
                            }
                        }
                    });

                    if (signUpError) {
                        throw signUpError;
                    }

                    set({ isLoading: false });
                    return { success: true, data };
                } catch (error) {
                    const errorMessage = error.message || 'Kayıt başarısız';
                    set({ error: errorMessage, isLoading: false });
                    throw error;
                }
            },

            // Update user profile
            updateUser: (userData) => {
                set((state) => ({
                    user: { ...state.user, ...userData }
                }));
            },

            // Change password
            changePassword: async (currentPassword, newPassword) => {
                try {
                    set({ isLoading: true, error: null });

                    // Use Supabase Auth to update password
                    const { error } = await supabase.auth.updateUser({
                        password: newPassword
                    });

                    if (error) throw error;

                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    const errorMessage = error.message || 'Şifre değiştirme başarısız';
                    set({ error: errorMessage, isLoading: false });
                    return { success: false, error: errorMessage };
                }
            },

            // Forgot password
            forgotPassword: async (email) => {
                try {
                    set({ isLoading: true, error: null });

                    const { error } = await supabase.auth.resetPasswordForEmail(email, {
                        redirectTo: `${window.location.origin}/reset-password`
                    });

                    if (error) throw error;

                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    const errorMessage = error.message || 'Şifre sıfırlama başarısız';
                    set({ error: errorMessage, isLoading: false });
                    return { success: false, error: errorMessage };
                }
            },

            // Reset password (with token from email link)
            resetPassword: async (newPassword) => {
                try {
                    set({ isLoading: true, error: null });

                    const { error } = await supabase.auth.updateUser({
                        password: newPassword
                    });

                    if (error) throw error;

                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    const errorMessage = error.message || 'Şifre sıfırlama başarısız';
                    set({ error: errorMessage, isLoading: false });
                    return { success: false, error: errorMessage };
                }
            },

            // Get current access token
            getAccessToken: () => {
                const session = get().session;
                return session?.access_token || null;
            },

            // Manually refresh session (usually automatic)
            refreshSession: async () => {
                try {
                    const { data, error } = await supabase.auth.refreshSession();

                    if (error) throw error;

                    set({ session: data.session });
                    api.defaults.headers.common['Authorization'] = `Bearer ${data.session.access_token}`;

                    return { success: true };
                } catch (error) {
                    console.error('Session refresh error:', error);
                    return { success: false };
                }
            },

            // Check if user has permission
            hasPermission: (requiredRole) => {
                const user = get().user;
                if (!user) return false;

                const roleHierarchy = {
                    admin: 5,
                    supervisor: 4,
                    team_leader: 3,
                    operator: 2,
                    paramedic: 1
                };

                const userRoleLevel = roleHierarchy[user.role] || 0;
                const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

                return userRoleLevel >= requiredRoleLevel;
            },

            // Check if user can access institution
            canAccessInstitution: (institutionId) => {
                const user = get().user;
                if (!user) return false;

                // Admin/Super Admin can access all institutions
                if (['admin', 'super_admin'].includes(user.role)) {
                    return true;
                }

                // Supervisors can only access their own institution
                if (user.role === 'supervisor') {
                    return user.institution_id === institutionId;
                }

                // Others limited access
                return user.institution_id === institutionId;
            },

            // Get user's institution info
            getUserInstitution: () => {
                const user = get().user;
                if (!user) return null;

                return {
                    id: user.institution_id,
                    name: user.institution_name,
                    code: user.institution_code
                };
            }
        }),
        {
            name: 'emt-crm-auth',
            partialize: (state) => ({
                user: state.user,
                session: state.session,
                isAuthenticated: state.isAuthenticated
            })
        }
    )
);

// Note: Auth initialization is handled in App.jsx via useEffect
// This prevents double initialization and ensures proper React lifecycle management

// Axios interceptor for automatic token handling
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Try to refresh session
            const refreshResult = await useAuthStore.getState().refreshSession();

            if (refreshResult.success) {
                // Retry the original request with new token
                const session = useAuthStore.getState().session;
                originalRequest.headers['Authorization'] = `Bearer ${session.access_token}`;
                return api(originalRequest);
            } else {
                // Refresh failed, logout user
                useAuthStore.getState().logout();
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);

export { useAuthStore };
export default useAuthStore;

